// components/KeyFeatures.js
import React, { useEffect, useState } from "react";
import sanityClient from "../sanityClient";

import detectIcon from "../assets/icons/detect.png";
import personalityIcon from "../assets/icons/personality.png";
import layoutIcon from "../assets/icons/layout.png";
import nudgeIcon from "../assets/icons/nudge.png";
import styled from "styled-components";

const KeyFeaturesSection = styled.section`
  width: 100%;
  background: #0c1c2c;
  padding: 64px 24px 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 48px 16px 80px;
  }

  @media (max-width: 480px) {
    padding: 32px 12px 60px;
  }
`;

const KeyLabel = styled.div`
  font-family: "Montserrat", sans-serif;
  font-weight: 600;
  font-size: 20px;
  letter-spacing: 2px;
  text-transform: uppercase;
  background: linear-gradient(
    106.71deg,
    #c69957 0%,
    #f7e096 47.6%,
    #a57632 93.27%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const KeyTitle = styled.h2`
  font-family: "Montserrat";
  font-weight: 700;
  font-size: 48px;
  line-height: 59px;
  color: #ffffff;
  text-align: center;
  margin: 16px 0;

  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 42px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    line-height: 34px;
  }
`;

const Subheadline = styled.p`
  font-family: Source Sans Pro;
  font-weight: 400;
  font-size: 20px;
  line-height: 32px;
  letter-spacing: 0%;
  text-align: center;
  color: #afb6d2;
  margin: 0 0 48px 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 18px;
    line-height: 28px;
    margin-bottom: 36px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    line-height: 26px;
    margin-bottom: 32px;
  }
`;
const FeaturesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 32px;
  margin-top: 32px;
  width: 100%;
  box-sizing: border-box;
`;

const FeatureCard = styled.div`
  width: 100%;
  max-width: 600px;
  min-height: 162px;
  perspective: 1000px;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const CardInner = styled.div`
  position: relative;
  width: 100%;
  min-height: 162px;
  transition: transform 0.6s;
  transform-style: preserve-3d;

  ${FeatureCard}:hover & {
    transform: rotateY(180deg);
  }

  @media (min-width: 769px) {
    width: 100%;
    ${FeatureCard}:hover & {
      transform: rotateY(180deg);
    }
  }
`;

const CardFront = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(94.99deg, #25313d 8.72%, #0d131a 98.58%);
  border: 2px solid #656565;
  border-radius: 32px;
  padding: 32px;
  display: flex;
  align-items: center;
  gap: 32px;
  backface-visibility: hidden;
`;

const CardBack = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    106.71deg,
    #c69957 0%,
    #f7e096 47.6%,
    #a57632 93.27%
  );
  border: 2px solid #fffb90;
  border-radius: 32px;
  padding: 32px;
  display: flex;
  align-items: center;
  gap: 32px;
  transform: rotateY(180deg);
  backface-visibility: hidden;
`;

const IconContainer = styled.div`
  width: 120px;
  height: 120px;
  background: rgba(0, 0, 0, 1);
  border-radius: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;

  img {
    width: 160px;
    height: 160px;
    object-fit: contain;
    object-position: center;
  }

  @media (max-width: 768px) {
    width: 120px;
    height: 120px;

    img {
      width: 140px;
      height: 140px;
    }
  }

  @media (max-width: 480px) {
    width: 100px;
    height: 100px;

    img {
      width: 120px;
      height: 120px;
    }
  }
`;

const IconContainerOverlay = styled.div`
  width: 120px;
  height: 120px;
  background: var(--Overlays-Default, #00000033);
  border-radius: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;

  img {
    width: 160px;
    height: 160px;
    object-fit: contain;
    object-position: center;
  }

  @media (max-width: 768px) {
    width: 120px;
    height: 120px;

    img {
      width: 140px;
      height: 140px;
    }
  }

  @media (max-width: 480px) {
    width: 100px;
    height: 100px;

    img {
      width: 120px;
      height: 120px;
    }
  }
`;

const FeatureTextOverlay = styled.div`
  max-width: 421px;
  font-family: "Montserrat";
  font-weight: 600;
  font-size: 22px;
  line-height: 120%;
  letter-spacing: 0%;
  color: #ffffff;

  @media (max-width: 768px) {
    font-size: 20px;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    max-width: 100%;
  }
`;

const FeatureText = styled.div`
  font-family: "Montserrat";
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  color: #000000;
  align-self: stretch;

  @media (max-width: 768px) {
    font-size: 20px;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    max-width: 100%;
  }
`;

const FeatureDescription = styled.div`
  font-family: "Source Sans Pro";
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 160%;
  color: #4c4c4c;
  align-self: stretch;

  @media (max-width: 768px) {
    font-size: 16px;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    max-width: 100%;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 421px;
  flex-grow: 1;

  @media (max-width: 768px) {
    gap: 6px;
  }

  @media (max-width: 480px) {
    gap: 2px;
  }
`;

const localIcons = [detectIcon, personalityIcon, layoutIcon, nudgeIcon];

const KeyFeatures = () => {
  const [flippedIndex, setFlippedIndex] = useState(null);
  const [featureSection, setFeatureSection] = useState(null);

  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    sanityClient
      .fetch(`*[_type == "featureSection"][0]`)
      .then(setFeatureSection)
      .catch((err) => console.error("❌ Error fetching feature section:", err));
  }, []);

  const handleCardClick = (index) => {
    if (isMobile) {
      setFlippedIndex((prev) => (prev === index ? null : index));
    }
  };

  const fallback = {
    sectionLabel: "Key Features",
    title: "Sell smarter with real-time behavioral AI",
    subheadline: "Neuora helps you spot high-intent shoppers instantly...",
    features: [
      {
        title: "Privacy-First (No Face Recognition)",
        description: "GDPR-compliant, pseudonymized behavioral signals",
      },
      {
        title: "Behavioral Recognition",
        description: "Dwell time, repeat visits, heatmaps & insights",
      },
      {
        title: "CRM & POS Integration Ready",
        description: "Multilingual Interface & Dashboard for store managers",
      },
      {
        title: "Live Nudging System",
        description: "Staff gets micro-recommendations in <3 sec",
      },
    ],
  };

  const section = featureSection || fallback;

  return (
    <KeyFeaturesSection>
      <KeyLabel>{section.sectionLabel}</KeyLabel>
      <KeyTitle>{section.title}</KeyTitle>
      <Subheadline>{section.subheadline}</Subheadline>

      <FeaturesGrid>
        {section.features.map((feature, index) => (
          <FeatureCard key={index}>
            <CardInner
              onClick={() => handleCardClick(index)}
              style={
                isMobile
                  ? {
                      transform:
                        flippedIndex === index
                          ? "rotateY(180deg)"
                          : "rotateY(0deg)",
                    }
                  : {}
              }
            >
              <CardFront>
                <IconContainerOverlay>
                  <img
                    src={localIcons[index % localIcons.length]}
                    alt={feature.title}
                  />
                </IconContainerOverlay>
                <FeatureTextOverlay>{feature.title}</FeatureTextOverlay>
              </CardFront>

              <CardBack>
                <IconContainer>
                  <img
                    src={localIcons[index % localIcons.length]}
                    alt={feature.title}
                  />
                </IconContainer>
                <CardContent>
                  <FeatureText>{feature.title}</FeatureText>
                  <FeatureDescription>{feature.description}</FeatureDescription>
                </CardContent>
              </CardBack>
            </CardInner>
          </FeatureCard>
        ))}
      </FeaturesGrid>
    </KeyFeaturesSection>
  );
};

export default KeyFeatures;
